'use client';

/**
 * **The value cell — the hover target, and the owner of the row's copy / clear
 * affordances** (LF-6359 / [ATTR-9], the prototype's *refined* treatment,
 * made the default by LF-6388 / [ATTR-10]).
 *
 * It is the production home of {@link ATTRIBUTE_CELL} /
 * {@link ATTRIBUTE_EDITOR_CHROME} / {@link ATTRIBUTE_EDITOR_BARE} — the three
 * tokens [ATTR-3] published for [ATTR-5] to choose from and which [ATTR-5]
 * never adopted, so the shared grid had drawn every open editor in the base
 * `Input` chrome ever since. [ATTR-8] compared *resting-state* renders at 672px,
 * and all of this differs only once an editor is open, so its measurements could
 * not see any of it.
 *
 * ## What moving the chrome onto the cell actually fixes
 *
 * Three corrections, each from a measurement in the prototype, and all three are
 * consequences of the chrome belonging to the **cell** rather than to the input:
 *
 * · **A ring, not a border.** A border adds a pixel to the box, so opening a
 *   field nudged its value 1px right and 1px down. A ring is drawn with
 *   box-shadow and occupies no layout, so the value does not move at all.
 * · **`slate-50`, the hover's own fill.** White made opening a field a jump to a
 *   *lighter* surface than the hover that preceded it, so the row appeared to
 *   flash on click.
 * · **The affordances survive the open.** They used to belong to the read-out
 *   *button* (`attribute-read-out.tsx`), which had two consequences: the icons
 *   sat outside the tint, and they vanished the moment the value was opened —
 *   so an open address popover or text editor lost its copy and clear buttons at
 *   exactly the point the value is being worked on. Owning both here fixes both:
 *   the tint is on the cell, the icons are inside it, and they render in the open
 *   state too.
 *
 * **Flex, never absolute positioning.** A shown icon takes real width and the
 * value shrinks around it, which is what makes *"the text is never underneath a
 * button"* true by construction rather than by a reserved-padding guess that
 * would have to be re-tuned every time an affordance is added.
 *
 * ## It is the default, and one surface still opts out
 *
 * This used to render only where {@link AttributeGrid} was passed `refined`,
 * i.e. only on the Contacts drawer — [ATTR-3] had noted that adopting the
 * chrome was *"a visual change to the shipped enquiry drawer rather than part
 * of this promotion"*, so the epic deferred it. LF-6388 / [ATTR-10] made that
 * change on purpose: the Contacts drawer, the Enquiries drawer, `/enquiries/{id}`
 * and the cost-agreement rail card all render through this cell now.
 *
 * The pre-unification path survives behind {@link AttributeGrid}'s
 * `legacyChrome`, which exactly one call site passes (`lead-matter-panel.tsx`,
 * the matter overview's enquiry band) until its own conversion ticket. There
 * this component is not mounted at all and the read-out keeps its own
 * affordances.
 */

import { useState, useSyncExternalStore } from 'react';
import { onlineManager } from '@tanstack/react-query';
import {
  CheckIcon,
  CloudOffIcon,
  CopyIcon,
  Loader2,
  XIcon
} from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  ATTRIBUTE_AFFORDANCE,
  ATTRIBUTE_AFFORDANCE_FADE,
  ATTRIBUTE_AFFORDANCE_REVEALED,
  ATTRIBUTE_CELL,
  ATTRIBUTE_CELL_EDITING,
  ATTRIBUTE_CELL_FAILED,
  ATTRIBUTE_CHROME_TARGET,
  ATTRIBUTE_EDITOR_CHROME,
  ATTRIBUTE_HOVER,
  ATTRIBUTE_VALUE_SLOT,
  ATTRIBUTE_VALUE_TARGET
} from './attribute-tokens';
import type { AttributeSpec } from './attribute-types';

/**
 * **The one place the two paths diverge** — the grid renders this and nothing
 * else has to know which treatment is in force.
 *
 * The held-back path (`legacyChrome`, one call site) is the plain
 * {@link ATTRIBUTE_VALUE_SLOT} div it has always been, with the read-out inside
 * carrying its own tint and affordances. The unified path — the default, and
 * every other surface — mounts {@link AttributeValueCell} instead.
 *
 * A component rather than a ternary at the call site because the alternative is
 * writing the row's three-way `boolean / editor / read-out` body out twice, once
 * per wrapper — which is exactly how the two paths would drift.
 */
export function AttributeValueSlot({
  unified,
  field,
  open,
  live,
  hugs = false,
  saving = false,
  failed = false,
  onClear,
  children
}: {
  unified: boolean;
  field: AttributeSpec<unknown>;
  open: boolean;
  live: boolean;
  /** See {@link AttributeValueCell.hugs} — chrome, not a grid row. */
  hugs?: boolean;
  /** See {@link AttributeValueCell.saving}. */
  saving?: boolean;
  /** See {@link AttributeValueCell.failed}. */
  failed?: boolean;
  onClear?: () => void;
  children: React.ReactNode;
}) {
  if (!unified)
    return (
      <div className={cn(ATTRIBUTE_VALUE_SLOT, 'items-center gap-1')}>
        {children}
        {/*
          The held-back path draws no cell, so the indicator hangs off the slot
          instead — the one thing about an in-flight save that is not chrome. It
          is the same component, so the two paths cannot drift on what "saving"
          looks like while both are alive.
        */}
        {saving ? <AttributeSavingIndicator label={field.label} /> : null}
      </div>
    );
  return (
    <AttributeValueCell
      field={field}
      open={open}
      live={live}
      hugs={hugs}
      saving={saving}
      failed={failed}
      onClear={onClear}
    >
      {children}
    </AttributeValueCell>
  );
}

/**
 * **Whether the save this row is waiting on can actually travel** (VALE-349).
 *
 * Bound to React Query's `onlineManager` rather than to `navigator.onLine`,
 * because the manager is not merely *a* reading of the same fact — it is **the
 * thing that decides**. The mutation layer runs `networkMode: 'online'`
 * (`lib/providers/query-provider.tsx`), so when the manager reports offline a
 * commit is not sent at all: React Query holds it, and the promise this package
 * awaits simply never settles. Reading the browser flag separately would be a
 * second definition of one fact, and the two would disagree the moment the
 * manager's own source changed (root `CLAUDE.md`, "bind to the source of
 * truth").
 *
 * Deliberately **not** `components/ai-scribe/recording-flow/use-online-status`:
 * that one is feature-scoped, and this package takes no feature dependencies.
 */
function useCommitsCanTravel(): boolean {
  return useSyncExternalStore(
    (onChange) => onlineManager.subscribe(() => onChange()),
    () => onlineManager.isOnline(),
    // Server render: assume online, so nothing renders an offline state into
    // markup the client would immediately correct.
    () => true
  );
}

/**
 * **A save still travelling, on the row it belongs to** (VALE-349).
 *
 * Rendered only once the save has passed
 * {@link ATTRIBUTE_SAVE_INDICATOR_DELAY_MS} — see that token for why a spinner
 * on every commit would be worse than none. It sits with the copy / clear
 * affordances rather than beside the value, so the value's width does not
 * depend on whether a request happens to be open.
 *
 * **Offline, it says so instead of spinning forever.** A spinner is a promise
 * that something is happening, and while the browser is offline nothing is: the
 * commit was never sent, and it will not be until the connection returns — at
 * which point React Query resumes it and the row settles on its own. A spinner
 * with no terminal state is the one thing worse than no indicator at all, so
 * the offline arm swaps the glyph and says what is actually true. **It does not
 * claim failure**: nothing failed, and the reader has nothing to retry.
 *
 * `role="status"` with the field named in it: this is the only feedback the
 * gesture produces, and a reader who cannot see the glyph gets nothing
 * otherwise.
 */
export function AttributeSavingIndicator({ label }: { label: string }) {
  const canTravel = useCommitsCanTravel();
  const field = label.toLowerCase();

  return (
    <span
      role="status"
      data-testid="attribute-saving-indicator"
      data-offline={!canTravel || undefined}
      title={
        canTravel
          ? undefined
          : `Waiting for a connection. ${label} will be saved when you are back online.`
      }
      className="flex h-5 w-5 shrink-0 items-center justify-center"
    >
      {canTravel ? (
        <Loader2
          aria-hidden
          className="text-foreground-subtle h-3.5 w-3.5 animate-spin"
        />
      ) : (
        <CloudOffIcon
          aria-hidden
          className="text-warning-subtle-foreground h-3.5 w-3.5"
        />
      )}
      <span className="sr-only">
        {canTravel
          ? `Saving ${field}…`
          : `Waiting for a connection. ${field} will be saved when you are back online.`}
      </span>
    </span>
  );
}

/**
 * **Which rows get a clear `×` — derived from the field, not opted into.**
 *
 * The affordance used to be `clearable: true` per spec, and the result was that
 * the same field type answered differently on two drawers: a `select` offered a
 * `×` for Contacts' `Role` and none for the enquiry's `Enquiry type`, and a
 * `date` offered one for `Date of birth` and none for `Date of enquiry`. That is
 * the same failure `legacyChrome` removed for the editor chrome — a shared
 * component whose behaviour depends on each call site remembering a prop — so it
 * is fixed the same way: derive the default, and name the exception.
 *
 * **`select` · `date` · `address` · `multiSelect` · `name` ·
 * `valueWithConfirmation` get it.** On those,
 * clearing otherwise means opening the control and hunting for a `Not recorded`
 * sentinel, or — for the two popover kinds — unchecking or emptying every part
 * one at a time (VALE-129). **`text` and `number` do not**: their editor
 * already clears by select-all-delete, so an icon would be a second route to
 * what the keyboard does. `boolean` is a live control with no empty state at
 * all.
 *
 * Two overrides, and each says a different thing:
 * `required` — there is no legal empty value (suppresses it);
 * `clearable` — force it onto a typed kind where deleting is a chore.
 */
export function offersClear(field: AttributeSpec<unknown>): boolean {
  // Before `clearable`, which would otherwise force a `×` onto a row that has
  // no commit path to clear through.
  if (field.readOnly) return false;
  if (field.required) return false;
  /*
    An explicit `null` suppresses the affordance outright — the row has nothing
    left to clear (VALE-1247). Checked here, with the other suppressions, so the
    `×` and what it would do can never disagree: the grid reads this function to
    decide whether to render the button at all.
  */
  if (field.clearOverride === null) return false;
  if (field.clearable) return true;
  /*
    **`triState` deliberately derives no `×`, and must not be given one.** The
    control already carries its own return path: clicking the lit segment
    un-answers the row, committing the vocabulary's own "not established" member.
    A `×` beside it would be a second gesture for one act, on a row whose whole
    argument is that answering should cost one click — and the two would have to
    agree about which value "cleared" means. See the `triState` arm in
    `attribute-types.ts`.
  */
  const { kind } = field.editor;
  return (
    kind === 'select' ||
    /*
      `selectWithCustom` earns it for `select`'s reason, unchanged (VALE-1227),
      and it is the `valueWithConfirmation` case rather than the `triState` one.
      Its vocabulary does carry a "nothing recorded" member — but so does the
      `Office` row's, and that row is a plain `select` that derives the `×`
      anyway. Withholding it here would be the surprise: two rows a reader
      cannot tell apart, one clearable and one not.

      It also has an argument `select` does not: the value a reader most wants
      to undo in one gesture is a **typed** one, and reaching "not recorded"
      from the input means opening the picker to find it.
    */
    kind === 'selectWithCustom' ||
    kind === 'date' ||
    kind === 'address' ||
    kind === 'multiSelect' ||
    kind === 'name' ||
    /*
      `bankAccount` earns it for the popover reason above, and more strongly than
      any other kind here: clearing an account otherwise means emptying up to
      five parts one at a time, and leaving any one of them behind leaves a
      half-account on the record — the exact state a reader clicking `×` is
      trying to get rid of (VALE-1211).
    */
    kind === 'bankAccount' ||
    /*
      `valueWithConfirmation` earns it for the popover reason above *and* one of its
      own: clearing the row is what **un-confirms the figure**. Without a `×` the
      only way back would be to open the popover and answer `No`, which is a
      gesture inside a gesture on the row a reader is most likely to have filled in
      by mistake (VALE-355).
    */
    kind === 'valueWithConfirmation'
    /*
      **`marketFigure` deliberately derives no `×`, and must not be given one**
      (VALE-1008). It is the `triState` case rather than the
      `valueWithConfirmation` one: there is no such thing as a cleared market
      figure. Vale owns the number unless the reader takes it over, so "empty" is
      not a state the record can hold. A `×` would either blank a figure the very
      next fetch puts back, which reads as the clear having failed, or it would
      have to mean *hand the figure back to Vale* — which is what answering the
      manual-entry toggle `No` already says, in the field that asks the question.
    */
  );
}

/**
 * **Which rows get a copy button — derived from the field, like {@link offersClear}.**
 *
 * `copyable: true` per spec had started down the same road `clearable` did: the
 * two drawers that render this component had each remembered it on the handful
 * of fields whoever wrote that spec happened to think of, so Contacts offered a
 * copy on `Email` and none on `Account name`, and the enquiry offered one on
 * `Phone` and none on any other text row. Identical component, arbitrary
 * behaviour — §2's failure one level down, and fixed the same way.
 *
 * **Every `text` row gets it, and so does `money`.** Free text is the shape
 * whose value is worth pasting somewhere else — a name into a form, an account
 * name into a banking portal, a note into an email — and it is the shape a
 * reader cannot re-derive by looking at it, unlike a `boolean` or a `select`
 * whose value is one word from a set they can already see. A currency figure
 * is the same shape: an inventory value is exactly the kind of thing someone
 * pastes into a spreadsheet or a distribution worksheet, which is why the
 * `number` note below no longer applies to it — this is that widening.
 *
 * Deliberately narrower than "every kind with a value":
 *
 * - **`select` and `boolean` do not get it.** Their value is a badge from a
 *   closed vocabulary; copying `Executor` to paste it somewhere is not a gesture
 *   anyone makes, and the icon would cost width on every row of both drawers.
 * - **`date` does not get it.** The read-out is a formatted date and the
 *   clipboard would get the `YYYY-MM-DD` draft — a copy that silently returns
 *   something other than what is on screen is worse than no copy.
 * - **`number` does not get it** for now. Contacts has no number row and the
 *   enquiry's two are short enough to retype; `money` is the one-token
 *   widening this note used to point at, now built.
 * - **`address` keeps opting in.** It is its own kind, and the one field whose
 *   {@link AttributeSpec.copyText} differs from its draft — the multi-line
 *   composed string rather than the structured value. A `money` spec should do
 *   the same: `field.draft` is the raw unformatted string this hook moves
 *   around, so a spec binding `copyable`'s clipboard text to `formatCurrency`
 *   (via `copyText`) is what puts `$1,234.00` on the clipboard rather than
 *   `1234`.
 *
 * **`required` does not suppress it, unlike on `offersClear`.** The two overrides
 * are asymmetric on purpose: clearing a required field is meaningless, but a
 * required `First name` is exactly the kind of value someone pastes elsewhere.
 *
 * `copyable` survives as the one override — force a copy onto a kind that does
 * not derive one.
 */
export function offersCopy(field: AttributeSpec<unknown>): boolean {
  if (field.copyable) return true;
  return (
    field.editor.kind === 'text' ||
    field.editor.kind === 'money' ||
    /*
      **`marketFigure` copies for exactly the `money` reason** (VALE-1008): a
      share holding's value is a currency figure someone pastes into a
      spreadsheet or a distribution worksheet, and the fact that Vale fetched it
      rather than a reader typing it changes nothing about that.
    */
    field.editor.kind === 'marketFigure' ||
    /*
      **`bankAccount` copies, and it is the shape most worth copying** — a BSB
      and an account number are transcribed into a banking portal or a payment
      form, digit for digit, which is precisely the error-prone act a copy button
      exists to remove (VALE-1211).

      Its spec must set `copyText`, because the fallback below (`field.draft`
      when it is a string) yields nothing for an object draft. `bankAccountCopyText`
      in `attribute-bank-account-editor.tsx` is the one definition of that payload:
      the two numbers space-separated, without the read-out's `·`, which a payment
      form would reject.
    */
    field.editor.kind === 'bankAccount'
  );
}

export function AttributeValueCell({
  field,
  open,
  live,
  hugs = false,
  saving = false,
  failed = false,
  onClear,
  children
}: {
  field: AttributeSpec<unknown>;
  /** This row's editor is the open one — the cell wears the editor chrome. */
  open: boolean;
  /** A live boolean control: no editor, and no affordances worth showing. */
  live: boolean;
  /**
   * **The cell sizes to its value rather than to a value column** — chrome, not
   * a grid row (VALE-349).
   *
   * {@link AttributeChromeValue} is the only caller that sets it: it renders one
   * attribute beside a drawer title, where filling the box would stretch a
   * `Status` pill across the whole header and read as an unstyled input. Every
   * grid row leaves it `false` and takes the column, which is what makes each
   * row's hit area the same shape as the one above it.
   */
  hugs?: boolean;
  /**
   * **This row's save has been travelling longer than
   * {@link ATTRIBUTE_SAVE_INDICATOR_DELAY_MS}** (VALE-349) — show the spinner.
   */
  saving?: boolean;
  /**
   * **This row's last commit was refused** (VALE-349) — wear
   * {@link ATTRIBUTE_CELL_FAILED}. The reason itself renders under the cell, in
   * the grid, where it has the row's own width to wrap in.
   */
  failed?: boolean;
  /**
   * Commits {@link AttributeSpec.clearTo}. Absent unless the field is
   * `clearable` — the grid routes it through the same commit path a typed edit
   * takes, so `validate` still gets to refuse it.
   */
  onClear?: () => void;
  children: React.ReactNode;
}) {
  const [copied, setCopied] = useState(false);

  const empty = field.display == null || field.display === '';
  const copyText =
    field.copyText ?? (typeof field.draft === 'string' ? field.draft : '');
  const showCopy = !live && offersCopy(field) && !empty && Boolean(copyText);
  /*
    Nothing to clear on an already-empty field: the affordance would be a `×`
    beside the `Set <label>…` placeholder it is meant to produce.
  */
  const showClear = !live && Boolean(onClear) && !empty;

  const copy = async (event: React.MouseEvent) => {
    event.stopPropagation();
    try {
      await navigator.clipboard.writeText(copyText);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard is unavailable outside a secure context. Silent by design:
      // the reader can still select the value, and a toast for a browser
      // capability she cannot change would be noise on every click.
    }
  };

  /*
    **The affordances hold their width at rest and fade in on hover** (VALE-493,
    reversing the `hidden` this used to carry). `hidden` cost the value nothing
    at rest and a full `h-5 w-5` pair on hover, so a wrapping value was measured
    against a *narrower* column the moment the pointer crossed it: on a contact
    with several lines of `Matter notes` the notes re-wrapped from 7 lines to 9,
    everything below moved down two lines, and the height change popped the
    drawer's scrollbar in — which narrowed every row again. A plain mouse-over
    visibly moved the whole panel.

    So the space is reserved permanently and only the paint changes. The accepted
    trade-off is that every row's value is permanently narrower by the affordance
    pair; a value that is the same width in both states is what removes the
    re-wrap, and with it the height change and the scrollbar pop.

    `pointer-events-none` while unrevealed, because reserved-but-invisible is
    exactly the shape that swallows a click meant for the value. A pointer
    reaching the button has already hovered the cell, so it is live by the time
    it can be clicked; on touch, where nothing hovers, the tap passes through to
    the value as it did when this was `hidden`. Keyboard is unaffected either way
    — and `opacity-0` is focusable, so tabbing now lands on the button directly
    instead of needing the group's focus to reveal it first.

    Always fully shown while the row is **open**, because then the reader is
    demonstrably working on this value and an icon that appears only on hover is
    one they have to go hunting for.

    `text-slate-500` at 14px, up from the held-back read-out's `slate-300` at
    12px. Two steps, because one was not enough: `slate-400` measured 2.6:1
    against the `slate-50` cell it now sits inside, under the 3:1 floor for a
    non-text control. `slate-500` clears it, and the icon growing to 14px does as
    much for legibility as the colour does.
  */
  const affordance = ATTRIBUTE_AFFORDANCE;
  const revealed = open
    ? 'opacity-100'
    : cn(ATTRIBUTE_AFFORDANCE_REVEALED, ATTRIBUTE_AFFORDANCE_FADE);

  return (
    <div
      className={cn(
        /*
          `pr-1` (inside `ATTRIBUTE_CELL`) so a revealed affordance's hover chip
          stops short of the cell's own edge instead of sitting on it — measured
          at 0px of gap before it was added, which is what made the chip read as
          overlapping the border.
        */
        ATTRIBUTE_CELL,
        /*
          **A grid row's target is the value column, at rest and open alike**
          (VALE-349) — so every row's hit area is the same shape whatever its
          value happens to say. Chrome hugs instead and takes the room back only
          to be typed in, which is what {@link ATTRIBUTE_CELL_EDITING} is left
          for. `live` is deliberately not in that condition: a boolean's
          segmented control is a fixed-size pair of buttons, so stretching the
          box around it would only add empty space.
        */
        hugs ? ATTRIBUTE_CHROME_TARGET : ATTRIBUTE_VALUE_TARGET,
        hugs && open && ATTRIBUTE_CELL_EDITING,
        // No tint on a row that does not open — a highlight is a promise.
        !open && !live && !field.readOnly && ATTRIBUTE_HOVER,
        /*
          The open editor's chrome belongs to the cell, not the input. Drawing it
          here means it encloses the value and its affordances together, exactly
          as the hover tint does, so clicking a row changes the surface's *state*
          and not its anatomy. The children render bare — see the `unified` arm of
          `AttributeFieldControl` and the `bare` arm of `AttributeReadOut`, both of
          which drop their own border for this reason.
        */
        open && !live && ATTRIBUTE_EDITOR_CHROME,
        /*
          **Last**, so a field that is both open and refused shows the refusal —
          which is the common case, since a refused commit is what keeps a field
          open in the first place (§2).
        */
        failed && ATTRIBUTE_CELL_FAILED
      )}
      data-testid="attribute-value-cell"
      data-open={open || undefined}
      /*
        The hook the leave-guard's *Keep editing* uses to put the reader back in
        the field it stopped them leaving — see `focusOpenAttributeEditor`. A
        data attribute rather than the `data-testid` above, because this one is
        behaviour and a test id must stay free to change.
      */
      data-attribute-open-editor={open && !live ? 'true' : undefined}
      data-failed={failed || undefined}
    >
      {children}
      {saving ? <AttributeSavingIndicator label={field.label} /> : null}
      {showCopy ? (
        <button
          type="button"
          onClick={copy}
          title={copied ? 'Copied' : `Copy ${field.label.toLowerCase()}`}
          /*
            The name changes with the state, not just the tooltip and the glyph.
            A copy is the one gesture here with no observable result — the value
            does not change, no editor opens, nothing saves — so the tick *is*
            the feedback. Leaving the name at `Copy phone` after it had copied
            meant a screen-reader user got no feedback at all, and a sighted
            reader got it from a `title` that is invisible on touch.
          */
          aria-label={
            copied
              ? `Copied ${field.label.toLowerCase()}`
              : `Copy ${field.label.toLowerCase()}`
          }
          className={cn(affordance, revealed)}
        >
          {copied ? (
            <CheckIcon className="text-success-subtle-foreground h-3.5 w-3.5 shrink-0" />
          ) : (
            <CopyIcon className="h-3.5 w-3.5 shrink-0" />
          )}
        </button>
      ) : null}
      {showClear ? (
        /*
          Clearing a dropdown or a date otherwise means opening it and hunting
          for a `Not recorded` sentinel — one this grid literally ships
          (`NONE = '__none'` in `./lead-field-specs`) because a Radix
          `SelectItem` cannot hold an empty value. A `×` on the row is the direct
          gesture for the same intent.

          `onClick`, deliberately not `onMouseDown`: it must not fire while the
          pointer is merely crossing the row on its way somewhere else.

          `stopPropagation` because the value button beside it opens the editor,
          and clearing a field must not also leave an editor open over the value
          it just emptied.
        */
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onClear?.();
          }}
          title={`Clear ${field.label.toLowerCase()}`}
          aria-label={`Clear ${field.label.toLowerCase()}`}
          className={cn(affordance, revealed)}
        >
          <XIcon className="h-3.5 w-3.5 shrink-0" />
        </button>
      ) : null}
    </div>
  );
}
